import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "hw_auth_token";

export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}
export function clearAuthToken() { setAuthToken(null); }

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ── In-memory GET cache (stale-while-revalidate) ─────────────────
// Why: the app feels slow because every page navigation re-fetches
// the same catalog endpoints (e.g. /api/sermons, /api/gospel-music,
// /api/apocrypha/books) — 200-800ms hits each time. With this cache
// the second visit to any page returns the cached payload IMMEDIATELY
// from RAM (sub-ms) and silently refetches in the background to
// refresh the cache for the next visit. Net effect: app feels
// instant on navigation, but still picks up server-side changes
// within 30 seconds of going stale.
//
// Scope:
//   • GET requests only (mutations always hit the network)
//   • Skips endpoints that must be live: auth/me, paywall/state,
//     sms/owner/health, owner/llm-health — anything that decides
//     based on the current user or rate-limited resource.
//   • TTL: 30 seconds. The browser still sees fresh data on every
//     reload (because RAM cache is per-session), but within a
//     single session a user bouncing between pages doesn't re-pay
//     the network cost for unchanged catalogs.
const _CACHE = new Map(); // key → { at, data }
const _CACHE_TTL_MS = 60_000;
// In-flight de-duplication: if the same GET is already on the wire,
// new callers share that one promise instead of opening a second
// identical request. The Home page (and a few rails) fire several
// concurrent catalog GETs on mount — without this, a fast double
// navigation or two components needing the same endpoint would each
// pay the full network round-trip. Keyed identically to the cache.
const _INFLIGHT = new Map(); // key → axios adapter promise
const _NEVER_CACHE = [
  "/auth/me",
  "/auth/login",
  "/auth/register",
  "/paywall/state",
  "/sms/owner/health",
  "/sms/status",
  "/owner/llm-health",
  "/owner/cloudinary-health",
  "/me/",
  "/me?",
  "/dm/",
  "/friends",
  "/users/directory",
];
function _shouldCache(url, method) {
  if ((method || "get").toLowerCase() !== "get") return false;
  if (!url) return false;
  // Don't cache `?play=…` URLs — they encode an action, not data.
  if (url.includes("play=") || url.includes("session_id=")) return false;
  return !_NEVER_CACHE.some((p) => url.includes(p));
}
function _cacheKey(config) {
  const u = config.url || "";
  // axios merges params into the URL at adapter time; build the same
  // shape ourselves so cached/uncached lookups line up.
  const p = config.params ? `?${new URLSearchParams(config.params).toString()}` : "";
  return `${u}${p}`;
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // Stale-while-revalidate: short-circuit to cached payload if we
  // have a fresh entry. axios convention: throwing an "adapter"
  // response from the request interceptor is awkward, so we use
  // a custom adapter when the cache is warm. The background refresh
  // is fired-and-forgotten so the next visit is fresh too.
  if (_shouldCache(config.url, config.method)) {
    const key = _cacheKey(config);
    const hit = _CACHE.get(key);
    if (hit && (Date.now() - hit.at) < _CACHE_TTL_MS) {
      // Background revalidate (don't await — let the cache age out
      // OR get refreshed by this silent fetch, whichever comes first).
      const stale = (Date.now() - hit.at) > (_CACHE_TTL_MS / 2);
      if (stale) {
        try {
          // Use a fresh axios instance so we don't recursively re-enter
          // this interceptor's cache short-circuit.
          axios.get((config.baseURL || "") + config.url, {
            params: config.params,
            headers: config.headers,
            withCredentials: true,
          }).then((r) => {
            _CACHE.set(key, { at: Date.now(), data: r.data, status: r.status, headers: r.headers });
          }).catch(() => {});
        } catch (_) {}
      }
      // Short-circuit by providing a synchronous adapter that returns
      // the cached payload.
      config.adapter = () => Promise.resolve({
        data: hit.data,
        status: hit.status || 200,
        statusText: "OK (cached)",
        headers: hit.headers || {},
        config,
        request: null,
      });
    }
  }
  return config;
});

api.interceptors.response.use((response) => {
  try {
    const cfg = response.config || {};
    if (_shouldCache(cfg.url, cfg.method) && response.status >= 200 && response.status < 300) {
      const key = _cacheKey(cfg);
      _CACHE.set(key, { at: Date.now(), data: response.data, status: response.status, headers: response.headers });
    }
  } catch (_) {}
  return response;
});

// ── In-flight request de-duplication adapter ─────────────────────
// Wrap axios's default adapter so concurrent identical GETs collapse
// into a single network request. Cache hits set `config.adapter`
// themselves (in the request interceptor) and bypass this entirely,
// so this only fires on a genuine cache MISS. Mutations and
// never-cache endpoints fall straight through to the real adapter.
const _defaultAdapter = axios.getAdapter(axios.defaults.adapter);
api.defaults.adapter = function dedupAdapter(config) {
  if (!_shouldCache(config.url, config.method)) {
    return _defaultAdapter(config);
  }
  const key = _cacheKey(config);
  const existing = _INFLIGHT.get(key);
  if (existing) return existing;
  const p = _defaultAdapter(config).finally(() => _INFLIGHT.delete(key));
  _INFLIGHT.set(key, p);
  return p;
};

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
